import { ProcedureStatus, WaypointActionStatusName, DbRef, DraftWaypointStatus, WaypointActionType, ItineraryStatus, WaypointStatusName, SampleStatus } from '@caresend/types';
import { hasStatus, initStatus, fetchWithRelationships, unbindByContext, getDraftWaypointPath, getWaypointActionPath, getProcedurePath, initTimeZoneDate, getItineraryPath, isNullish } from '@caresend/utils';
import dayjs from 'dayjs';
import { usePortalStore } from '../index.js';
import { getFetchWithRelationshipsCallbacks, getItineraryRelationships } from '../relationships.js';
import { fixRoutes } from './fix.js';
import { setWaypointActionProcedureReferences, commitAndDeleteDraftShipments, addWaypointActionsReferencesToWaypoints } from './helpers.js';
import { deletePlaceReferenceFromSamples, setProcedureSamplesStatuses } from './procedure.js';
import { useDbSyncStore } from '../../storeUtilities/dbSync/dbSyncStore.js';
import { getStore } from '../../../../../../store/helpers/initStore.js';
import { initRoute } from '../../../../../../store/modules/itineraries/helpers.js';

/* eslint-disable no-console */
/** ROUTE RELATED */
/**
 * Add new route to the end of the itinerary routes
 */
const addRoute = (route) => {
    const portalStore = usePortalStore();
    const db = useDbSyncStore();
    const { itinerary } = portalStore;
    const routes = [...(itinerary.value.routes || []), route];
    const routeIndex = routes.length - 1;
    db.queueNextUpdate(getItineraryPath(itinerary.value.id, `routes/${routeIndex}`), 'add route', route, DbRef.ITINERARIES);
    itinerary.set.routes(routes);
};
/**
 * Set route duration for route at index and update db
 */
const setRouteDuration = (routeIndex, duration) => {
    const portalStore = usePortalStore();
    const { itinerary } = portalStore;
    const { routes } = itinerary.value;
    const route = routes === null || routes === void 0 ? void 0 : routes[routeIndex];
    if (!route)
        return;
    portalStore.itinerary.set.routes([{
            ...route,
            duration,
        }], 'update route duration');
};
/**
 * Remove route by waypoint ID
 *
 * Utilized internally
 */
const unsetRouteByWaypointID = (waypointID) => {
    const portalStore = usePortalStore();
    const { itinerary } = portalStore;
    const routes = [...(itinerary.value.routes || [])];
    const routeIndex = routes === null || routes === void 0 ? void 0 : routes.findIndex((route) => route.to.id === waypointID);
    if (routeIndex === -1 || isNullish(routeIndex))
        return;
    routes.splice(routeIndex, 1);
    portalStore.itinerary.set.routes(routes, 'remove route');
    fixRoutes();
};
/** WAYPOINT RELATED */
/**
 * Add new or update existing waypoint to the itinerary
 */
const addOrUpdateWaypoint = (waypoint, 
/** Optional index to insert the waypoint at */
index) => {
    const portalStore = usePortalStore();
    const { itinerary } = portalStore;
    const waypoints = [...itinerary.value.waypoints];
    const hasWaypoint = waypoints.some(({ id }) => id === waypoint.id);
    const labelPrefix = hasWaypoint ? 'update' : 'create';
    portalStore.waypoints.set(waypoint, `${labelPrefix} waypoint`);
    if (hasWaypoint)
        return;
    let itineraryLabelPrefix = '';
    if (!isNullish(index)) {
        waypoints.splice(index, 0, waypoint);
        itineraryLabelPrefix = 'insert';
    }
    else {
        waypoints.push({ id: waypoint.id });
        itineraryLabelPrefix = 'create';
    }
    itinerary.set.waypoints(waypoints, `${itineraryLabelPrefix} waypoint references on itinerary`);
};
/** WAYPOINT ACTION RELATED */
/**
 * Add or update waypoint action.
 * If indicated, commit shipments to db and delete from state.
 */
const addOrUpdateWaypointAction = (waypointAction, commitShipments = false) => {
    const portalStore = usePortalStore();
    const existingWpa = portalStore.waypointActions.getRaw(waypointAction.id);
    /** For mail-in, set draft shipments in db and delete from state */
    if (commitShipments)
        commitAndDeleteDraftShipments(waypointAction);
    /** Update procedure references */
    setWaypointActionProcedureReferences(waypointAction);
    /** Update waypoint references */
    addWaypointActionsReferencesToWaypoints([waypointAction]);
    /** Write waypoint action to db */
    const writePrefix = existingWpa ? 'update' : 'create';
    portalStore.waypointActions.set(waypointAction, `${writePrefix} waypoint action`);
};
/**
 * Update waypoint action
 */
const addWaypointActions = (waypointActions, commitShipments = false) => {
    waypointActions.forEach((waypointAction) => {
        addOrUpdateWaypointAction(waypointAction, commitShipments);
    });
};
/**
 * Remove waypoint actions by IDs and/or by waypoint ID
 */
const removeWaypointActionsAndMaybeWaypoint = (_waypointActionIDs, _waypointID, 
/** Whether to skip removing waypoint action references from waypoints */
skipWaypointReferences = false) => {
    var _a, _b;
    const portalStore = usePortalStore();
    const db = useDbSyncStore();
    const wp = portalStore.waypoints.getRaw(_waypointID !== null && _waypointID !== void 0 ? _waypointID : '');
    const waypointActionIDs = [...(_waypointActionIDs !== null && _waypointActionIDs !== void 0 ? _waypointActionIDs : [])];
    if (_waypointID && wp) {
        /** Waypoint is being deleted, so remove references to waypoint actions */
        const wpas = (_b = (_a = wp.waypointActions) === null || _a === void 0 ? void 0 : _a.map(({ id }) => id)) !== null && _b !== void 0 ? _b : [];
        waypointActionIDs.push(...wpas);
        /**
         * Remove waypoint and itinerary references from draft waypoints
         * and reset status to finalized
         */
        const { draftWaypoints } = wp;
        Object.keys(draftWaypoints !== null && draftWaypoints !== void 0 ? draftWaypoints : {}).forEach((draftWaypointID) => {
            db.queueNextUpdate(getDraftWaypointPath(draftWaypointID, 'waypointID'), 'delete waypoint reference from draft waypoint', null, DbRef.ITINERARIES);
            db.queueNextUpdate(getDraftWaypointPath(draftWaypointID, 'itineraryID'), 'delete itinerary reference from draft waypoint', null, DbRef.ITINERARIES);
            db.queueNextUpdate(getDraftWaypointPath(draftWaypointID, 'status'), 'reset draft waypoint status to finalized', DraftWaypointStatus.FINALIZED, DbRef.ITINERARIES);
        });
        unsetRouteByWaypointID(wp.id);
        /** Remove waypoint from itinerary */
        const newWps = portalStore.itinerary.value.waypoints
            .filter(({ id }) => id !== wp.id);
        portalStore.itinerary.set.waypoints(newWps, 'delete waypoint reference from itinerary');
        /** Remove waypoint from state */
        portalStore.waypoints.unset(wp.id, 'delete waypoint');
    }
    /** IDs of waypoints that have waypoint actions removed */
    const waypointRefs = {};
    waypointActionIDs.forEach((id) => {
        var _a, _b;
        const waypointAction = portalStore.waypointActions.getRaw(id);
        if (!waypointAction)
            return;
        db.queueNextUpdate(getWaypointActionPath(id), 'delete waypoint action', null, DbRef.ITINERARIES);
        const procedureIDs = Object.keys((_a = waypointAction.procedures) !== null && _a !== void 0 ? _a : {});
        procedureIDs.forEach((procedureID) => {
            db.queueNextUpdate(getProcedurePath(procedureID, `waypointActions/${id}`), 'delete waypoint action reference from procedure', null, DbRef.ITINERARIES);
            db.queueNextUpdate(getProcedurePath(procedureID, 'nurseID'), 'remove nurse reference from procedure', null, DbRef.ITINERARIES);
            if (waypointAction.type === WaypointActionType.DROPOFF
                || waypointAction.type === WaypointActionType.MAIL_IN) {
                deletePlaceReferenceFromSamples(procedureID);
            }
        });
        if (waypointAction.waypointID) {
            /** Mark waypoint as having a waypoint action removed */
            waypointRefs[waypointAction.waypointID] = [
                ...((_b = waypointRefs[waypointAction.waypointID]) !== null && _b !== void 0 ? _b : []),
                id,
            ];
        }
        portalStore.waypointActions.unset(id, 'delete waypoint action');
    });
    if (!skipWaypointReferences) {
        /** Remove waypoint action references from waypoints */
        Object.entries(waypointRefs).forEach(([waypointID, wpaIDs]) => {
            const waypoint = portalStore.waypoints.getRaw(waypointID);
            if (!(waypoint === null || waypoint === void 0 ? void 0 : waypoint.waypointActions))
                return;
            const newWpas = waypoint.waypointActions
                .filter(({ id: wpaID }) => !wpaIDs.includes(wpaID));
            portalStore.waypoints.setChildOf(waypointID, 'waypointActions', newWpas, 'delete waypoint action reference from waypoint');
        });
    }
};
/**
 * Set duration for a specific waypoint action
 */
const setWaypointActionDuration = (waypointActionID, duration) => {
    const portalStore = usePortalStore();
    const waypointAction = portalStore.waypointActions.getRaw(waypointActionID);
    if (!waypointAction)
        return;
    /** TODO: remove this once setChildOf is implemented to support nested keys */
    portalStore.waypointActions.set({
        ...waypointAction,
        duration: {
            ...waypointAction.duration,
            actionDuration: duration,
        },
    });
    portalStore.waypointActions.setChildOf(waypointActionID, 'duration/actionDuration', duration, 'set waypoint action duration');
};
/**
 * Set status for a specific waypoint action
 */
const setWaypointActionStatus = (waypointActionID, status) => {
    const portalStore = usePortalStore();
    const waypointAction = portalStore.waypointActions.getRaw(waypointActionID);
    if (!waypointAction)
        return;
    const newStatus = initStatus(status);
    portalStore.waypointActions.setChildOf(waypointActionID, 'status', newStatus, 'update waypoint action status');
    if (status === WaypointActionStatusName.COMPLETE) {
        const { procedures } = waypointAction;
        if (!procedures)
            return;
        Object.keys(procedures).forEach((procedureID) => {
            setProcedureSamplesStatuses(procedureID, SampleStatus.COMPLETED);
        });
    }
};
const maybeCancelWaypointAction = (waypointActionID) => {
    const portalStore = usePortalStore();
    const waypointAction = portalStore.waypointActions.getRaw(waypointActionID);
    if (!waypointAction)
        return;
    const procedures = portalStore.waypointActions.getters.procedures(waypointAction);
    if (!procedures)
        return;
    const allProceduresCanceled = Object.values(procedures).every((procedure) => hasStatus(procedure, ProcedureStatus.CANCELED));
    if (!allProceduresCanceled)
        return;
    portalStore.waypointActions.setChildOf(waypointActionID, 'status', initStatus(WaypointActionStatusName.CANCELED), 'cancel waypoint action');
};
/**
 * Set total duration for a specific waypoint and distribute it among its actions
 */
const setWaypointTotalDuration = (waypointID, duration) => {
    const portalStore = usePortalStore();
    const waypoint = portalStore.waypoints.getRaw(waypointID);
    if (!waypoint || !waypoint.waypointActions)
        return;
    const distributedDuration = Math.round(duration / waypoint.waypointActions.length);
    waypoint.waypointActions.forEach(({ id }) => {
        setWaypointActionDuration(id, distributedDuration);
    });
};
/**
 * Save itinerary
 */
const saveItinerary = () => {
    const portalStore = usePortalStore();
    const db = useDbSyncStore();
    const { itinerary } = portalStore;
    if (hasStatus(itinerary.value, ItineraryStatus.DRAFT)) {
        itinerary.set.status(ItineraryStatus.CREATED, 'update itinerary status');
    }
    itinerary.getters.currentWaypoints().forEach((waypoint) => {
        var _a, _b;
        (_a = waypoint.waypointActions) === null || _a === void 0 ? void 0 : _a.forEach(({ id: wpaID }) => {
            const waypointAction = portalStore.waypointActions.getRawClone(wpaID);
            if (!waypointAction)
                return;
            if (hasStatus(waypointAction, WaypointActionStatusName.DRAFT)) {
                setWaypointActionStatus(wpaID, WaypointActionStatusName.INCOMPLETE);
            }
            setWaypointActionProcedureReferences(waypointAction);
        });
        if (hasStatus(waypoint, WaypointStatusName.DRAFT)) {
            portalStore.waypoints.setChildOf(waypoint.id, 'status', initStatus(WaypointStatusName.IN_ITINERARY), 'update waypoint status');
        }
        Object.keys((_b = waypoint.draftWaypoints) !== null && _b !== void 0 ? _b : {}).forEach((draftWaypointID) => {
            const draftWaypoint = portalStore.draftWaypoints.getRawClone(draftWaypointID);
            if (!draftWaypoint)
                return;
            draftWaypoint.status = DraftWaypointStatus.IN_ITINERARY;
            db.queueNextUpdate(getDraftWaypointPath(draftWaypointID, 'status'), 'update draft waypoint status', draftWaypoint.status, DbRef.ITINERARIES);
            draftWaypoint.waypointID = waypoint.id;
            db.queueNextUpdate(getDraftWaypointPath(draftWaypointID, 'waypointID'), 'set draft waypoint waypoint ID', draftWaypoint.waypointID, DbRef.ITINERARIES);
            draftWaypoint.itineraryID = itinerary.value.id;
            db.queueNextUpdate(getDraftWaypointPath(draftWaypointID, 'itineraryID'), 'set draft waypoint itinerary ID', draftWaypoint.itineraryID, DbRef.ITINERARIES);
        });
    });
};
/**
 * Add and merge a waypoint and its waypoint actions with an existing waypoint
 */
const addAndMergeWithWaypoint = (waypoint, inputWpas, existingWaypointID) => {
    var _a;
    const portalStore = usePortalStore();
    const existingWaypoint = portalStore.waypoints.getRawClone(existingWaypointID);
    if (!existingWaypoint)
        return;
    /**
     * Keep track of the input records for potential merging
     * and remove the input record once it is merged. If not
     * merged, the input record will be used as is.
     */
    const inputWpaRecord = Object.fromEntries(inputWpas.map((wpa) => ([wpa.id, {
            ...wpa,
            waypointID: existingWaypointID,
        }])));
    const existingWpas = portalStore.waypoints.getters.waypointActions(existingWaypointID);
    inputWpas.forEach((wpa) => {
        var _a;
        const existingWpa = existingWpas.find((_wpa) => portalStore.waypointActions.getters.combinableMatch(wpa, _wpa));
        if (!existingWpa)
            return;
        /** Combine any instructions */
        const instructions = existingWpa.instructions
            ? `${existingWpa.instructions}\n${(_a = wpa.instructions) !== null && _a !== void 0 ? _a : ''}`
            : wpa.instructions;
        if (instructions !== existingWpa.instructions) {
            portalStore.waypointActions.setChildOf(existingWpa.id, 'instructions', instructions, 'merge waypoint action instructions');
        }
        /** Combine shipments */
        const shipments = {
            ...existingWpa.shipments,
            ...wpa.shipments,
        };
        if (Object.keys(shipments).length > 0) {
            portalStore.waypointActions.setChildOf(existingWpa.id, 'shipments', shipments, 'merge waypoint action shipments');
        }
        /** Combine procedures */
        const procedures = {
            ...existingWpa.procedures,
            ...wpa.procedures,
        };
        if (Object.keys(procedures).length > 0) {
            portalStore.waypointActions.setChildOf(existingWpa.id, 'procedures', procedures, 'merge waypoint action procedures');
        }
        /** Remove the input waypoint action from the input record */
        delete inputWpaRecord[wpa.id];
    });
    // Plug in existingWaypointID into the draftWaypoints
    const { draftWaypoints } = waypoint;
    const newDraftWaypoints = {
        ...existingWaypoint.draftWaypoints,
        ...draftWaypoints,
    };
    portalStore.waypoints.setChildOf(existingWaypoint.id, 'draftWaypoints', newDraftWaypoints, 'merge draft waypoints');
    const newWpas = [
        ...(_a = existingWaypoint.waypointActions) !== null && _a !== void 0 ? _a : [],
        ...Object.keys(inputWpaRecord).map((id) => ({ id })),
    ];
    portalStore.waypoints.setChildOf(existingWaypoint.id, 'waypointActions', newWpas, 'merge waypoint actions');
    // Update the existing waypoint in the store
    addOrUpdateWaypoint(existingWaypoint);
    // Add remaining waypoint actions
    addWaypointActions(Object.values(inputWpaRecord));
    // Fix routes if necessary
    fixRoutes();
};
/**
 * Add a waypoint at a specific index in the itinerary
 */
const addWaypointAtIndex = async (waypoint, waypointActions, index) => {
    const portalStore = usePortalStore();
    // Check if the waypoint already exists in the state
    if (portalStore.itinerary.getters.currentWaypoints().find((wp) => wp.id === waypoint.id))
        return;
    // Add or update the waypoint at the specified index
    addOrUpdateWaypoint(waypoint, index);
    // Add waypoint actions associated with this waypoint
    waypointActions.forEach((waypointAction) => {
        addOrUpdateWaypointAction(waypointAction);
    });
    // If there are more than one waypoint, add a route between the last two waypoints
    const currentDraftLength = portalStore.itinerary.value.waypoints.length;
    if (currentDraftLength > 1) {
        const prevWaypoint = portalStore.itinerary.value.waypoints[currentDraftLength - 2];
        if (!prevWaypoint)
            throw new Error('Previous waypoint not found.');
        const route = initRoute(waypoint.id, prevWaypoint.id);
        addRoute(route);
    }
    // If the added waypoint is the first one, set the itinerary start time
    const waypointIndex = portalStore.itinerary.value.waypoints.length - 1;
    if (waypointIndex === 0 && waypoint.date) {
        const startOfDay = dayjs(waypoint.date.timestamp)
            .tz(waypoint.date.timeZone)
            .startOf('day');
        portalStore.itinerary.set.startTime(initTimeZoneDate(waypoint.date.timeZone, waypoint.date.timestamp), true);
        portalStore.itinerary.set.dateInfo({
            date: initTimeZoneDate(waypoint.date.timeZone, startOfDay.valueOf()),
        }, true);
    }
};
const ITINERARY_BIND_CONTEXT = 'itinerary';
const SHIFT_BIND_CONTEXT = 'shift';
/**
 * Bind itinerary and its dependencies
 */
const bindItineraryAndDependencies = async (itineraryID, withLoadingIDs = true) => {
    var _a, _b, _c;
    const portalStore = usePortalStore();
    const store = getStore();
    const relationships = getItineraryRelationships();
    const defaultCallbacks = getFetchWithRelationshipsCallbacks(itineraryID);
    const writers = {
        ...defaultCallbacks,
        loadingIDs: withLoadingIDs
            ? defaultCallbacks.loadingIDs
            : undefined,
    };
    portalStore.state.set.isItineraryFound(false);
    try {
        const response = await fetchWithRelationships(DbRef.ITINERARIES, itineraryID, relationships, { batchingContextID: ITINERARY_BIND_CONTEXT, bind: true, writers });
        /**
         * TODO: investigate why procedures are not provided in the callback reliably
         * upon mount
         */
        const procedures = response[DbRef.PROCEDURES];
        if (procedures)
            portalStore.procedures.update(procedures);
        if (!((_a = response[DbRef.ITINERARIES]) === null || _a === void 0 ? void 0 : _a[itineraryID])
            && portalStore.itinerary.value.id !== itineraryID) {
            portalStore.init({
                id: itineraryID,
                userID: (_b = store.state.auth.user) === null || _b === void 0 ? void 0 : _b.id,
                // TODO: Determine if we ever need to make new itineraries if they don't exist
                queueUpdate: false,
            });
        }
        portalStore.state.set.isItineraryFound(!!((_c = response[DbRef.ITINERARIES]) === null || _c === void 0 ? void 0 : _c[itineraryID]));
    }
    catch (error) {
        console.error('Error binding itinerary and dependencies:', error);
    }
    portalStore.state.set.isReady(true);
};
const unbindItineraryAndDependencies = () => {
    unbindByContext(ITINERARY_BIND_CONTEXT);
};
const bindShiftAndDependencies = async (shiftID) => {
    const writers = getFetchWithRelationshipsCallbacks(shiftID);
    await fetchWithRelationships(DbRef.SHIFTS, shiftID, {
        itineraries: {
            waypoints: {
                waypointActions: {
                    placeGroupID: true,
                    procedures: {
                        placeAccounts: true,
                    },
                },
            },
        },
    }, { batchingContextID: SHIFT_BIND_CONTEXT, bind: true, writers });
};
const unbindShiftAndDependencies = () => {
    unbindByContext(SHIFT_BIND_CONTEXT);
};

export { addAndMergeWithWaypoint, addOrUpdateWaypoint, addOrUpdateWaypointAction, addRoute, addWaypointActions, addWaypointAtIndex, bindItineraryAndDependencies, bindShiftAndDependencies, maybeCancelWaypointAction, removeWaypointActionsAndMaybeWaypoint, saveItinerary, setRouteDuration, setWaypointActionDuration, setWaypointActionStatus, setWaypointTotalDuration, unbindItineraryAndDependencies, unbindShiftAndDependencies };
